import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db';
import type { Log } from '../types';
import { toast } from 'react-hot-toast';

const LOG_TYPE_COLORS = {
  scan: 'text-blue-600 bg-blue-50',
  modify: 'text-yellow-600 bg-yellow-50',
  settings: 'text-purple-600 bg-purple-50',
  import: 'text-green-600 bg-green-50',
  complete: 'text-teal-600 bg-teal-50',
  backup: 'text-indigo-600 bg-indigo-50',
  restore: 'text-pink-600 bg-pink-50',
};

const LOG_TYPE_LABELS = {
  scan: '스캔',
  modify: '수정',
  settings: '설정',
  import: '가져오기',
  complete: '완료',
  backup: '백업',
  restore: '복원',
};

export default function LogViewer() {
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState<string | null>(null);
  const logsPerPage = 20;

  // 실시간 로그 데이터 가져오기
  const logs = useLiveQuery(async () => {
    let query = db.logs.orderBy('timestamp').reverse();
    if (filterType) {
      query = query.filter((log) => log.type === filterType);
    }
    return query
      .offset((page - 1) * logsPerPage)
      .limit(logsPerPage)
      .toArray();
  }, [page, filterType]);

  const totalLogs = useLiveQuery(() => db.logs.count(), []);

  const totalPages = totalLogs ? Math.ceil(totalLogs / logsPerPage) : 1;

  const formatMetadata = (metadata: Record<string, any> | undefined) => {
    if (!metadata) return null;
    return Object.entries(metadata).map(([key, value]) => (
      <div key={key} className="flex space-x-2">
        <span className="font-medium">{key}:</span>
        <span>{typeof value === 'object' ? JSON.stringify(value) : value}</span>
      </div>
    ));
  };

  if (!logs || !totalLogs) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">작업 이력</h2>
          <div className="flex items-center space-x-3">
            <select
              value={filterType || ''}
              onChange={(e) => {
                setFilterType(e.target.value || null);
                setPage(1);
              }}
              className="px-3 py-1 border rounded-md text-sm"
            >
              <option value="">모든 유형</option>
              {Object.entries(LOG_TYPE_LABELS).map(([type, label]) => (
                <option key={type} value={type}>
                  {label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-200">
        {logs.map((log) => (
          <div key={log.id} className="p-4 hover:bg-gray-50">
            <div className="flex items-start justify-between">
              <div className="flex items-center space-x-3">
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    LOG_TYPE_COLORS[log.type]
                  }`}
                >
                  {LOG_TYPE_LABELS[log.type]}
                </span>
                <span className="text-sm text-gray-900">{log.message}</span>
              </div>
              <time className="text-xs text-gray-500">
                {format(new Date(log.timestamp), 'PPP p', { locale: ko })}
              </time>
            </div>
            {(log.skuId || log.metadata) && (
              <div className="mt-2 text-xs text-gray-500">
                {log.skuId && (
                  <div>
                    <span className="font-medium">SKU ID:</span> {log.skuId}
                  </div>
                )}
                {formatMetadata(log.metadata)}
              </div>
            )}
          </div>
        ))}

        {logs.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            로그 데이터가 없습니다
          </div>
        )}
      </div>

      {logs.length > 0 && (
        <div className="p-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            총 {totalLogs.toLocaleString()}개 로그 (페이지 {page}/{totalPages})
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
            >
              이전
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border rounded-md text-sm disabled:opacity-50"
            >
              다음
            </button>
          </div>
        </div>
      )}
    </div>
  );
}